import { Injectable } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { SEARCH_STRING_PARAM } from '@ecs-features/search/search.component';
import { SuccessfulSearchesService } from '@ecs-services/search/search-help/successful-searches.service';
import { isSerialNumberResult, SEARCH_TERM_SEPARATOR } from '@ecs-services/search/search.helper';
import { SearchResultType } from '@ecs-services/search/search.interface';
import { SearchService } from '@ecs-services/search/search.service';
import { searchTermChanged } from '@ecs-stores/store.helper';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, filter, first, map, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { SearchStoreService } from '../../services/search/search-store.service';
import { LoadingLayerService } from './../../services/loading-layer.service';
import { SearchEffectsService } from './search-effects.service';
import * as SearchActions from './search.actions';

@Injectable()
export class SearchEffects {
    public triggerSearch$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SearchActions.requestSearchResult),
            // TODO: verify what kind of caching behavior we need to implement.
            //       for now, disable all caching and request from backend everytime the search is submitted
            // distinctUntilChanged(({ payload: { searchTerm: prev } }, { payload: { searchTerm: curr } }) =>
            //     arrayEqualsShallow(prev, curr)
            // ),
            map(({ payload: { searchTerm } }) =>
                SearchActions.requestSearchResultDistinct({ payload: { searchTerm } })
            )
        )
    );

    public resetOrderNumberSearchResult$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SearchActions.requestSearchResultDistinct),
            map(() => this.resetOrderNumberSearchResult())
        )
    );

    public requestSearchResult$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SearchActions.requestSearchResultDistinct),
            switchMap(({ payload: { searchTerm } }) =>
                this.searchService.getSearchResult(searchTerm).pipe(
                    takeUntil(searchTermChanged(this.searchStore)),
                    catchError(() => of(null)),
                    map((response) => {
                        if (response) {
                            return SearchActions.receiveSearchResult({
                                payload: {
                                    searchResult: response.result,
                                    searchError: null,
                                },
                                userRequested: !response.triggeredByLanguageChange,
                            });
                        }

                        return this.getNoResultsError(searchTerm);
                    })
                )
            )
        )
    );

    public requestSearchResultElectronics$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SearchActions.requestSearchResultElectronics),
            switchMap(({ payload: { searchTerm } }) =>
                this.searchService.getSearchResult(searchTerm).pipe(
                    takeUntil(searchTermChanged(this.searchStore)),
                    catchError(() => of(null)),
                    map((response) => {
                        if (response) {
                            const isElectronics =
                                isSerialNumberResult(response.result) && response.result.isCabinetInverter;
                            if (isElectronics) {
                                return SearchActions.receiveSearchResult({
                                    payload: {
                                        searchResult: response.result,
                                        searchError: null,
                                    },
                                    userRequested: !response.triggeredByLanguageChange,
                                });
                            }
                        }

                        return this.getNoElectronicsResultsError(searchTerm);
                    })
                )
            )
        )
    );

    public requestSearchHistoryInitial$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SearchActions.updateSearchHistory),
            filter(({ initial }) => initial),
            switchMap(() =>
                this.latestSearchResultsService
                    .getLatestSuccessfulSearchTerms(10)
                    .pipe(catchError(() => of([])))
            ),
            map((response) =>
                SearchActions.updateSearchHistory({
                    payload: { searchHistory: response },
                    initial: false,
                })
            )
        )
    );

    public setSearchStringParam$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(SearchActions.requestSearchResultDistinct),
                tap(({ payload: { searchTerm } }) => {
                    const queryParams = {} as Params;
                    queryParams[SEARCH_STRING_PARAM] = searchTerm.join(SEARCH_TERM_SEPARATOR);
                    // Forward QueryParams to result-page (not only SEARCH_STRING)
                    this.activatedRoute.snapshot.queryParamMap.keys.forEach((key) => {
                        if (key !== SEARCH_STRING_PARAM) {
                            queryParams[key] = this.activatedRoute.snapshot.queryParamMap.get(key);
                        }
                    });

                    this.router.navigate([''], { queryParams });
                })
            ),
        { dispatch: false }
    );

    /**
     * Actions to perform when receiving a search result,
     * namely routing and storing the search term in the search history.
     */
    public receiveSearchResult$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SearchActions.receiveSearchResult),
            withLatestFrom(this.searchStore.getSearchTerm()),
            map(
                ([
                    {
                        payload: { searchResult },
                        userRequested,
                    },
                    searchTerms,
                ]) => {
                    if (!searchResult || !userRequested) {
                        return SearchActions.noOpAction();
                    }

                    switch (searchResult.type) {
                        case SearchResultType.SERIALNUMBER:
                        case SearchResultType.SPARE_PART_LIST:
                            this.router.navigate(['/product'], {
                                queryParamsHandling: 'preserve',
                                skipLocationChange: true,
                            });

                            return SearchActions.updateSearchHistory({
                                payload: {
                                    searchHistory: [
                                        searchResult.type === SearchResultType.SERIALNUMBER
                                            ? searchResult.serialNumber
                                            : searchResult.id,
                                    ],
                                },
                                initial: false,
                            });

                        case SearchResultType.SPARE_PARTS:
                            if (!searchResult.spareParts?.length) {
                                // clear result display (router outlet)
                                this.router.navigate([{ outlets: { result: null } }], {
                                    queryParamsHandling: 'preserve',
                                    skipLocationChange: true,
                                });

                                // While the http request was successful, it returned no displayable spare parts.
                                // Therefore, we have to show an error.
                                const sparePartsError = searchResult.errors ?? [];

                                return this.getNoResultsError(sparePartsError, true);
                            }

                            this.router.navigate([{ outlets: { result: ['spare-parts'] } }], {
                                queryParamsHandling: 'preserve',
                                skipLocationChange: true,
                            });

                            return SearchActions.updateSearchHistory({
                                payload: {
                                    searchHistory: [searchResult.spareParts.join(SEARCH_TERM_SEPARATOR)],
                                },
                                initial: false,
                            });

                        case SearchResultType.FREE_TEXT:
                            this.router.navigate([{ outlets: { result: ['spare-parts'] } }], {
                                queryParamsHandling: 'preserve',
                                skipLocationChange: true,
                            });

                            return SearchActions.updateSearchHistory({
                                payload: {
                                    searchHistory: [searchTerms.join(SEARCH_TERM_SEPARATOR)],
                                },
                                initial: false,
                            });

                        case SearchResultType.TYPE_DESIGNATION:
                            if (searchResult.sparePartLists) {
                                this.router.navigate([{ outlets: { result: ['type-designation'] } }], {
                                    queryParamsHandling: 'preserve',
                                    skipLocationChange: true,
                                });
                            } else if (searchResult.spareParts) {
                                /**
                                 * Treat `TypeDesignationSearchResult` without `documents`-Property and with `spareParts`-Property like `FreeTextSearchResult`.
                                 */
                                this.router.navigate([{ outlets: { result: ['spare-parts'] } }], {
                                    queryParamsHandling: 'preserve',
                                    skipLocationChange: true,
                                });
                            }

                            return SearchActions.updateSearchHistory({
                                payload: {
                                    searchHistory: [searchTerms.join(SEARCH_TERM_SEPARATOR)],
                                },
                                initial: false,
                            });

                        default:
                            return SearchActions.noOpAction();
                    }
                }
            )
        )
    );

    public updateOrderNumberSearchHistory$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SearchActions.receiveOrderNumberSearchResult),
            map(({ payload: { orderNumberSearchResult } }) => orderNumberSearchResult),
            filter((orderNumberSearchResult) => orderNumberSearchResult !== null),
            map(() => this.searchStore.getSearchTerm()),
            switchMap((searchTermsObs) =>
                searchTermsObs.pipe(
                    first(),
                    map((searchTerms) =>
                        SearchActions.updateSearchHistory({
                            payload: {
                                searchHistory: [searchTerms.join(SEARCH_TERM_SEPARATOR)],
                            },
                            initial: false,
                        })
                    )
                )
            )
        )
    );

    // TODO: Might never do something useful. Remove?
    public guessSearchResultType$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(SearchActions.requestSearchResultDistinct),
                tap(({ payload: { searchTerm } }) => {
                    // TODO: move to dedicated service when more result types are implemented
                    const singleSearchTerm = searchTerm.length === 1 && searchTerm[0];

                    if (singleSearchTerm && singleSearchTerm[2] === '.') {
                        // product
                        return console.log('show product page skeleton');
                    } else if (singleSearchTerm && /(?:[0-9][1-9][0-9]{7})/.test(singleSearchTerm)) {
                        // part list
                        return console.log('show part list skeleton');
                    }

                    return console.log('show result table');
                })
            ),
        // TODO: dispatch action and show skeleton in separate effect?
        { dispatch: false }
    );

    public showLoaderOnSearch$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(SearchActions.requestSearchResultDistinct),
                tap(() => this.loadingLayerService.showLayer())
            ),
        { dispatch: false }
    );

    public hideLoaderOnReceive$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(SearchActions.receiveSearchResult),
                tap(() => this.loadingLayerService.hideLayer())
            ),
        { dispatch: false }
    );

    public constructor(
        private actions$: Actions,
        private searchService: SearchService,
        private latestSearchResultsService: SuccessfulSearchesService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private loadingLayerService: LoadingLayerService,
        private searchEffectsService: SearchEffectsService,
        private searchStore: SearchStoreService
    ) {}

    private getNoResultsError(searchTerm: string[], needToCheckForOrderItems = false) {
        return this.searchEffectsService.getNoResultsError(searchTerm, needToCheckForOrderItems);
    }

    private getNoElectronicsResultsError(searchTerm: string[]) {
        return this.searchEffectsService.getNoElectronicsResultsError(searchTerm);
    }

    private resetOrderNumberSearchResult() {
        return SearchActions.receiveOrderNumberSearchResult({
            payload: {
                orderNumberSearchResult: undefined,
            },
        });
    }
}
